import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { PreferenceStoreIdentifier } from '@transcend-io/privacy-types';

import type { PrivacyCenterTrackingPurposePreview } from '@main/cm-types';
import type { PurposeSchema } from '@main/sombra-types';

/**
 * Purposes state
 */
export interface PurposesState {
  /** The privacyCenter purposes configuration */
  configuration?: PrivacyCenterTrackingPurposePreview[];
  /** The user's preferences */
  preferences?: Record<string, PurposeSchema[] | undefined>;
  /** The user's identifiers by partition */
  identifiers?: Record<string, PreferenceStoreIdentifier[] | undefined>;
}

export const purposesSlice = createSlice({
  name: 'Purposes',
  initialState: {} as PurposesState,
  reducers: {
    setPrivacyCenterPurposeConfiguration: (
      state,
      {
        payload: configuration,
      }: PayloadAction<PrivacyCenterTrackingPurposePreview[]>,
    ) => ({
      ...state,
      configuration,
    }),
    resetPrivacyCenterPurposeConfiguration: (state) => ({
      ...state,
      configuration: undefined,
    }),
    setPrivacyCenterPurposePreferences: (
      state,
      {
        payload,
      }: PayloadAction<{
        /** Partition */
        partition: string;
        /** User's preferences */
        preferences: PurposeSchema[];
      }>,
    ) => ({
      ...state,
      preferences: {
        ...state.preferences,
        [payload.partition]: payload.preferences,
      },
    }),
    resetPrivacyCenterPurposePreferences: (state) => ({
      ...state,
      preferences: undefined,
    }),
    resetPrivacyCenterPurposePreferencesForPartition: (
      state,
      {
        payload,
      }: PayloadAction<{
        /** Partition */
        partition: string;
      }>,
    ) => ({
      ...state,
      preferences: {
        ...state.preferences,
        [payload.partition]: undefined,
      },
    }),
    setPrivacyCenterUserIdentifiers: (
      state,
      {
        payload,
      }: PayloadAction<{
        /** Partition */
        partition: string;
        /** User's preferences */
        identifiers: PreferenceStoreIdentifier[];
      }>,
    ) => ({
      ...state,
      identifiers: {
        ...state.identifiers,
        [payload.partition]: payload.identifiers,
      },
    }),
    resetPrivacyCenterUserIdentifiers: (state) => ({
      ...state,
      identifiers: undefined,
    }),
    resetPrivacyCenterUserIdentifiersForPartition: (
      state,
      {
        payload,
      }: PayloadAction<{
        /** Partition */
        partition: string;
      }>,
    ) => ({
      ...state,
      identifiers: {
        ...state.identifiers,
        [payload.partition]: undefined,
      },
    }),
  },
});

export const {
  setPrivacyCenterPurposeConfiguration,
  resetPrivacyCenterPurposeConfiguration,
  setPrivacyCenterPurposePreferences,
  resetPrivacyCenterPurposePreferences,
  resetPrivacyCenterPurposePreferencesForPartition,
  setPrivacyCenterUserIdentifiers,
  resetPrivacyCenterUserIdentifiers,
  resetPrivacyCenterUserIdentifiersForPartition,
} = purposesSlice.actions;
